export default () => ({
  isOpen: false,

  init() {
    this.$watch('isOpen', (value: boolean) => {
      document.body.classList.toggle('overflow-hidden', value)
    })

    // Ignored while YouTube's own fullscreen player is active: that Escape press should exit
    // fullscreen only, not also close the modal underneath it.
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !this.isOpen || document.fullscreenElement) return

      this.close()
    })

    // Exiting fullscreen leaves focus on the iframe, so a follow-up Escape never reaches the
    // listener above (cross-origin iframes don't bubble keydown to the parent document at all).
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement && this.isOpen) (this.$root as HTMLElement).focus()
    })

    // Capture phase: clicking an inactive slide scrolls to it instead of reaching lite-youtube's
    // click-to-play listener. Matched on `li > div`, not `li`, since the `<li>` keeps its full
    // unscaled layout box — matching it would catch clicks in the empty margin around a shrunk slide.
    ;(this.$refs.carouselWrapper as HTMLElement).addEventListener('click', (event: MouseEvent) => {
      const card = (event.target as HTMLElement).closest('li > div')

      if (!card) return

      const slide = card.parentElement as HTMLElement

      if (slide.classList.contains('is-active')) return

      const index = Array.from(slide.parentElement!.children).indexOf(slide)

      event.stopPropagation()
      this._carousel().scrollTo(index)
    }, true)
  },

  open(index: number) {
    this.isOpen = true

    this.$nextTick(() => {
      // The carousel is initialized while hidden (x-show), so Embla measured zero-width slides at init time.
      this._carousel().reInit()
      this._carousel().scrollTo(index, true)
      this._activate(index)
    })
  },

  _carousel() {
    const carouselEl = (this.$refs.carouselWrapper as HTMLElement).querySelector('[x-data]') as HTMLElement

    return (window as any).Alpine.$data(carouselEl)
  },

  close() {
    this.isOpen = false

    this.$root.querySelectorAll('lite-youtube.lyt-activated').forEach((el: Element) => {
      el.querySelector('iframe')?.remove()
      el.classList.remove('lyt-activated')
    })
  },

  onSelect(index: number) {
    this._activate(index)
  },

  _activate(index: number) {
    const items = this.$el.querySelectorAll('lite-youtube')
    const root = this.$root as HTMLElement

    items.forEach((el: Element, i: number) => {
      if (i === index) {
        if (el.classList.contains('lyt-activated')) {
          this._sendCommand(el as HTMLElement, 'playVideo')
        } else {
          // lite-youtube-embed focuses its own iframe once activated, which would otherwise
          // swallow a later Escape press — reclaim focus for the modal's keydown listener.
          ;(el as any).activate().then(() => root.focus())
        }
      } else {
        this._sendCommand(el as HTMLElement, 'pauseVideo')
      }
    })
  },

  // postMessage command API (requires `enablejsapi=1`), used only when switching between
  // already-activated slides so the inactive ones stay mounted for Embla's slide references.
  _sendCommand(el: HTMLElement, func: 'playVideo' | 'pauseVideo') {
    const iframe = el.querySelector('iframe') as HTMLIFrameElement | null

    iframe?.contentWindow?.postMessage(JSON.stringify({ event: 'command', func, args: [] }), '*')
  },
})
